/**
 * Glowwa consumer trials — scroll-driven card animation.
 *
 * Each item has a tall scroll track. The sticky pin holds the image in the
 * viewport for the full scroll duration. Stat cards travel from off-screen
 * bottom to off-screen top as the user scrolls, with a small stagger
 * between cards on desktop.
 *
 * Desktop: image centered in the pin by flex, never translates.
 * Mobile: image starts at the top of the pin (small gap to the "The
 * Results" button). As the section scrolls up into view, JS translates the
 * cluster down so the image lingers — the gap between button and image
 * opens as the user scrolls. By the time the pin's sticky engages, the
 * image has reached the centered position, so there's no jump.
 */

(function () {
  'use strict';

  const ROOT    = '[data-glowwa-consumer-trials]';
  const ITEM    = '[data-glowwa-ct-item]';
  const SCROLL  = '[data-glowwa-ct-scroll]';
  const PIN     = '[data-glowwa-ct-pin]';
  const CLUSTER = '[data-glowwa-ct-cluster]';
  const MEDIA   = '[data-glowwa-ct-media]';
  const STATS   = '[data-glowwa-ct-stats]';
  const STAT    = '[data-glowwa-ct-stat]';

  const DESKTOP_MQ = window.matchMedia('(min-width: 990px)');
  const REDUCED_MQ = window.matchMedia('(prefers-reduced-motion: reduce)');

  // Fraction of total scroll progress by which each successive card is
  // delayed. Card i starts moving at progress = i * CARD_STAGGER.
  const CARD_STAGGER = 0.12;

  // Mobile pre-sticky drift: fraction of viewport height (measured from the
  // top down) over which the image cluster drifts from pin-top to centered.
  // Lower value → drift starts later (subtler). At 0.5 the image sits at pin
  // top until the pin has moved past the midpoint of the viewport, then
  // drifts down to centered by the time it reaches viewport top.
  const IMAGE_DRIFT_RANGE = 0.5;

  let ticking = false;
  let listenersBound = false;

  /** @type {WeakMap<HTMLElement, object>} */
  let measureCache = new WeakMap();

  /** @type {ResizeObserver | null} */
  let mediaResizeObserver = null;

  /** @type {WeakSet<HTMLElement>} */
  const observedMedia = new WeakSet();

  /** @type {WeakSet<HTMLElement>} */
  const observedRoots = new WeakSet();

  // ── Helpers ──────────────────────────────────────────────────────────────

  /** @param {number} v @param {number} lo @param {number} hi */
  function clamp(v, lo, hi) { return Math.min(hi, Math.max(lo, v)); }

  /**
   * The pin sticks at `top: var(--header-height)`, not `top: 0` — the
   * theme's sticky header permanently covers that many px at the top of the
   * viewport. When truly stuck, pin.getBoundingClientRect().top settles at
   * this value, not 0, so anything comparing pinRect.top against "stuck"
   * needs to subtract it first. Read live (not cached) since it can change
   * responsively (e.g. announcement bar toggling).
   *
   * IMPORTANT: read this from the pin element itself (or any element inside
   * <body>), not document.documentElement. CSS custom properties only
   * inherit downward, and the theme sets --header-height on <body> (or a
   * wrapper), not <html> — so getComputedStyle(document.documentElement)
   * will never see it and silently falls back to 0, even though the CSS
   * var(--header-height) on .glowwa-ct__item-pin resolves correctly via
   * inheritance. Reading from the same element CSS uses keeps JS and CSS in
   * sync by construction.
   * @param {HTMLElement} el
   * @returns {number}
   */
  function getHeaderOffset(el) {
    const raw = getComputedStyle(el).getPropertyValue('--header-height');
    const px = parseFloat(raw);
    return Number.isFinite(px) ? px : 0;
  }

  /** @param {number} a @param {number} b @param {number} t */
  function lerp(a, b, t) { return a + (b - a) * t; }

  /** @param {HTMLElement} card */
  function getCardRotation(card) {
    const inline = card.style.getPropertyValue('--glowwa-ct-stat-rotate');
    if (inline) return inline.trim();
    const computed = getComputedStyle(card).getPropertyValue('--glowwa-ct-stat-rotate');
    return computed.trim() || '0deg';
  }

  /**
   * @param {HTMLElement} card
   * @param {number} y
   * @param {boolean} isDesktop
   */
  function setCardTransform(card, y, isDesktop) {
    const rotate = isDesktop ? getCardRotation(card) : '0deg';
    card.style.transform = `translate3d(0, ${y}px, 0) rotate(${rotate})`;
  }

  /** @param {HTMLElement} stats @param {number} y */
  function setGroupTransform(stats, y) {
    stats.style.transform = `translate3d(0, ${y}px, 0)`;
  }

  /** @param {HTMLElement} item */
  function setRestingState(item) {
    const cluster = item.querySelector(CLUSTER);
    const stats   = item.querySelector(STATS);
    item.classList.add('is-at-rest');
    if (cluster instanceof HTMLElement) cluster.style.removeProperty('transform');
    if (stats instanceof HTMLElement) stats.style.removeProperty('transform');
    item.querySelectorAll(STAT).forEach(card => {
      if (card instanceof HTMLElement) card.style.removeProperty('transform');
    });
  }

  /** @param {HTMLElement} item */
  function clearRestingState(item) {
    item.classList.remove('is-at-rest');
  }

  // ── Measurement ──────────────────────────────────────────────────────────

  /**
   * Measure the hidden (below) and exit (above) Y offsets for cards/group.
   * Temporarily resets transforms so rects are accurate.
   *
   * @param {HTMLElement} item
   * @param {boolean} isDesktop
   */
  function measureItem(item, isDesktop) {
    const pin   = item.querySelector(PIN);
    const stats = item.querySelector(STATS);
    if (!(pin instanceof HTMLElement) || !(stats instanceof HTMLElement)) {
      return { group: { hiddenY: 0, exitY: 0 }, cards: [] };
    }

    const cards = [...stats.querySelectorAll(STAT)].filter(el => el instanceof HTMLElement);

    // Save current transforms.
    const savedStats = stats.style.transform;
    const savedCards = cards.map(c => c.style.transform);

    // Reset to natural layout for measurement.
    stats.style.removeProperty('transform');
    cards.forEach(card => {
      const rot = isDesktop ? getCardRotation(card) : '0deg';
      card.style.transform = rot === '0deg' ? '' : `rotate(${rot})`;
    });

    const pinRect   = pin.getBoundingClientRect();
    const statsRect = stats.getBoundingClientRect();
    const BUF = 32;

    const group = {
      hiddenY: pinRect.bottom - statsRect.top + BUF,
      exitY:   pinRect.top   - statsRect.bottom - BUF,
    };

    const cardMetrics = cards.map(card => {
      const r = card.getBoundingClientRect();
      return {
        hiddenY: pinRect.bottom - r.top + BUF,
        exitY:   pinRect.top   - r.bottom - BUF,
      };
    });

    // Restore transforms.
    stats.style.transform = savedStats;
    cards.forEach((card, i) => { card.style.transform = savedCards[i]; });

    return { group, cards: cardMetrics };
  }

  function invalidateMeasures() { measureCache = new WeakMap(); }

  /**
   * @param {HTMLElement} item
   * @param {boolean} isDesktop
   */
  function getCachedMetrics(item, isDesktop) {
    const pin   = item.querySelector(PIN);
    const media = item.querySelector(MEDIA);
    const pinH  = pin instanceof HTMLElement ? pin.offsetHeight : 0;
    const mediaH = media instanceof HTMLElement ? media.offsetHeight : 0;
    const cached = measureCache.get(item);
    if (
      cached &&
      cached.isDesktop === isDesktop &&
      cached.pinHeight === pinH &&
      cached.mediaHeight === mediaH
    ) {
      return cached;
    }
    const metrics = measureItem(item, isDesktop);
    measureCache.set(item, { isDesktop, pinHeight: pinH, mediaHeight: mediaH, ...metrics });
    return metrics;
  }

  // ── Scroll progress ───────────────────────────────────────────────────────

  /**
   * Progress 0→1 across the pin's sticky lifetime — i.e. sticky duration =
   * scrollTrack.height - pin.height. Pin may be shorter than the viewport on
   * mobile (square image fits inside a smaller pin), so we can't use vh here.
   * The pin sticks at top: headerOffset (below the theme's sticky header),
   * not top: 0, so progress must hit 0 when scrollTrack's top reaches
   * headerOffset — not when it reaches the true viewport top.
   *
   * @param {HTMLElement} scrollTrack
   * @param {HTMLElement} pin
   * @param {number} headerOffset
   * @returns {number} 0–1
   */
  function getScrollProgress(scrollTrack, pin, headerOffset) {
    const range = scrollTrack.offsetHeight - pin.offsetHeight;
    if (range <= 0) return 1;
    const rect = scrollTrack.getBoundingClientRect();
    return clamp(-(rect.top - headerOffset) / range, 0, 1);
  }

  // ── Per-item update ───────────────────────────────────────────────────────

  /** @param {HTMLElement} item */
  function updateItem(item) {
    const scrollTrack = item.querySelector(SCROLL);
    const pin         = item.querySelector(PIN);
    const stats       = item.querySelector(STATS);

    if (
      !(scrollTrack instanceof HTMLElement) ||
      !(pin instanceof HTMLElement) ||
      !(stats instanceof HTMLElement)
    ) return;

    const vh        = window.innerHeight;
    const trackRect = scrollTrack.getBoundingClientRect();

    if (trackRect.bottom <= 0 || trackRect.top >= vh) {
      setRestingState(item);
      return;
    }

    if (REDUCED_MQ.matches) {
      setRestingState(item);
      return;
    }

    clearRestingState(item);

    const isDesktop     = DESKTOP_MQ.matches;
    const headerOffset  = getHeaderOffset(pin);
    const progress      = getScrollProgress(scrollTrack, pin, headerOffset);
    const { group, cards: cardMetrics } = getCachedMetrics(item, isDesktop);
    const cards = [...stats.querySelectorAll(STAT)].filter(el => el instanceof HTMLElement);
    const FBH = vh;
    const FBE = -vh;

    if (isDesktop) {
      // Desktop: each card moves independently with a stagger offset.
      stats.style.removeProperty('transform');
      cards.forEach((card, i) => {
        const metric  = cardMetrics[i];
        const hiddenY = metric?.hiddenY ?? FBH;
        const exitY   = metric?.exitY   ?? FBE;
        // Card i begins its travel at progress = i * CARD_STAGGER.
        const start  = i * CARD_STAGGER;
        const cardT  = clamp((progress - start) / (1 - start), 0, 1);
        setCardTransform(card, lerp(hiddenY, exitY, cardT), isDesktop);
      });
    } else {
      // Mobile: pre-sticky parallax on the image cluster. CSS already centers
      // the media box inside the (always full pin height) cluster, so the
      // resting transform is 0. Before drift completes, pull the cluster up
      // by centerOffset so the image starts flush at the top of the pin,
      // then ease that pull-up out to 0 as the pin's top moves from the
      // viewport bottom up toward headerOffset (where the pin actually
      // sticks — not the true viewport top). Once stuck, the offset is
      // fully eased out, landing on center — this works for any aspect
      // ratio since centerOffset is derived from the actual pin/media
      // height difference (0 if media fills the pin).
      const cluster = item.querySelector(CLUSTER);
      const media   = item.querySelector(MEDIA);
      if (cluster instanceof HTMLElement && media instanceof HTMLElement) {
        const pinRect      = pin.getBoundingClientRect();
        const centerOffset = Math.max(0, (pin.offsetHeight - media.offsetHeight) / 2);
        const driftT       = clamp(1 - (pinRect.top - headerOffset) / (vh * IMAGE_DRIFT_RANGE), 0, 1);
        const appliedY      = -centerOffset * (1 - driftT);
        cluster.style.transform = `translate3d(0, ${appliedY}px, 0)`;
      }

      // Move the whole stats group as one unit.
      const hiddenY = group.hiddenY || FBH;
      const exitY   = group.exitY   || FBE;
      setGroupTransform(stats, lerp(hiddenY, exitY, progress));
      cards.forEach(card => {
        if (card instanceof HTMLElement) card.style.removeProperty('transform');
      });
    }
  }

  // ── Global update ─────────────────────────────────────────────────────────

  function updateAll() {
    document.querySelectorAll(ROOT).forEach(root => {
      root.querySelectorAll(ITEM).forEach(item => {
        if (item instanceof HTMLElement) updateItem(item);
      });
    });
  }

  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => { updateAll(); ticking = false; });
  }

  function onResize() {
    invalidateMeasures();
    onScroll();
  }

  // ── Boot ─────────────────────────────────────────────────────────────────

  function bindListeners() {
    if (listenersBound) return;
    listenersBound = true;
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });
    window.addEventListener('orientationchange', onResize, { passive: true });
    DESKTOP_MQ.addEventListener('change', onResize);
    REDUCED_MQ.addEventListener('change', onScroll);
  }

  /**
   * Aspect-ratio changes update media height without a window resize.
   * Observe media boxes and root inline-style updates from the theme editor.
   */
  function observeLayoutTargets() {
    if (typeof ResizeObserver === 'undefined') return;

    if (!mediaResizeObserver) {
      mediaResizeObserver = new ResizeObserver(() => {
        onResize();
      });
    }

    document.querySelectorAll(ROOT).forEach(root => {
      if (!(root instanceof HTMLElement)) return;

      if (!observedRoots.has(root)) {
        observedRoots.add(root);
        const styleObserver = new MutationObserver(() => {
          onResize();
        });
        styleObserver.observe(root, {
          attributes: true,
          attributeFilter: ['style'],
        });
      }

      root.querySelectorAll(MEDIA).forEach(media => {
        if (media instanceof HTMLElement && !observedMedia.has(media)) {
          observedMedia.add(media);
          mediaResizeObserver.observe(media);
        }
      });
    });
  }

  function boot() {
    bindListeners();
    observeLayoutTargets();
    invalidateMeasures();
    document.querySelectorAll(`${ROOT} img`).forEach(img => {
      if (img instanceof HTMLImageElement && !img.complete) {
        img.addEventListener('load', onResize, { once: true });
      }
    });
    onScroll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('shopify:block:select', onScroll);
})();
